import { connect } from 'react-redux';
import { keyBy, get, orderBy, isEmpty } from 'lodash';

import * as XLSX from 'xlsx';
import { v4 as uuidV4 } from 'uuid';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useState, useEffect, useMemo } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal } from '@actions/modal';
import { showErrorMessage } from '@actions/messageconfirmation';

import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_CONFIRM_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';

import agentService from '@services/agent';

import ConversationList from './components/ConversationList';
import Header from './components/Header';
import InputArea from './components/InputArea';
import MessageList from './components/MessageList';

import { parseMarkdownTable } from './utils/excel';

import { ChatContainer, ChatMessagesContainer } from './styledComponents';

const INTERVAL_POLLING_MESSAGES = 3000;
const PROCESSING_MESSAGE_STATUS = 'processing';

const InpulseAgent = (props) => {
  const { showErrorMessage, openGenericModal, currency, userRights } = props;

  const [conversations, setConversations] = useState({});
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [isConversationLoading, setIsConversationLoading] = useState(true);
  const [dailyUsage, setDailyUsage] = useState(0);
  const [isPollingNecessary, setIsPollingNecessary] = useState(false);
  const [isRetryInProgress, setIsRetryInProgress] = useState(false);

  const _formatFetchedConversations = (fetchedConversations) =>
    fetchedConversations.map((conversation) => ({
      ...conversation,
      id: conversation.id,
      name: conversation.name,
      createdAt: moment.utc(conversation.created_at),
      updatedAt: moment.utc(conversation.updated_at),
      messages: {},
    }));

  const _formatFetchedMessages = (fetchedMessages) =>
    fetchedMessages.reduce((acc, message) => {
      acc[message.id] = {
        id: message.id,
        content: message.content,
        isUser: message.is_user,
        status: message.status,
        createdAt: moment.utc(message.created_at),
        chart: message.chart,
        score: message.score,
        table: message.table,
        isLocalMessage: false,
        has_retried: message.has_retried || false,
      };

      return acc;
    }, {});

  const enabledFeaturePaths = useMemo(
    () => (userRights || []).map(({ path }) => path).filter((path) => path != null),
    [userRights],
  );

  useEffect(() => {
    (async () => await fetchConversations())();
  }, []);

  useEffect(() => {
    setInputValue('');

    if (
      selectedConversationId &&
      !get(conversations, `${selectedConversationId}.isNewConversation`, false)
    ) {
      (async () => await fetchConversationMessages())();
    }
  }, [selectedConversationId]);

  useEffect(() => {
    if (!(selectedConversationId && conversations[selectedConversationId])) {
      setIsPollingNecessary(false);
      return;
    }

    const conversation = conversations[selectedConversationId];
    const messages = Object.values(conversation.messages);

    const orphanedMessages = messages.filter(
      ({ status, isLocalMessage }) => status === PROCESSING_MESSAGE_STATUS && !isLocalMessage,
    );

    setIsPollingNecessary(orphanedMessages.length > 0);
  }, [selectedConversationId, conversations]);

  const pollForNewMessages = async () => {
    try {
      const { messages: fetchedMessages, conversation_name: conversationName } =
        await agentService.getConversationMessages(selectedConversationId);
      const formattedMessages = _formatFetchedMessages(fetchedMessages);

      const formattedMessagesValues = Object.values(formattedMessages);

      if (formattedMessagesValues.some(({ status }) => status === PROCESSING_MESSAGE_STATUS)) {
        return;
      }

      setConversations({
        ...conversations,
        [selectedConversationId]: {
          ...conversations[selectedConversationId],
          messages: formattedMessages,
          name: conversationName,
        },
      });

      setIsRetryInProgress(
        formattedMessagesValues.some(
          ({ status, has_retried }) => status === PROCESSING_MESSAGE_STATUS && has_retried,
        ),
      );
    } catch {}
  };

  useEffect(() => {
    if (!isPollingNecessary || !selectedConversationId) {
      return;
    }

    const pollingInterval = setInterval(pollForNewMessages, INTERVAL_POLLING_MESSAGES);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [isPollingNecessary, selectedConversationId]);

  const fetchConversations = async () => {
    setIsConversationLoading(true);

    try {
      const response = await agentService.getConversations();
      const fetchedConversations = response.conversations;
      const dailyUsage = response.daily_usage;

      setDailyUsage(dailyUsage);

      if (fetchedConversations.length === 0) {
        const newConversation = {
          id: uuidV4(),
          name: i18next.t('AGENT.AGENT.NEW_CHAT'),
          messages: {},
          createdAt: moment(new Date().toJSON()),
          updatedAt: moment(new Date().toJSON()),
          isNewConversation: true,
          active: true,
          favorite: false,
        };

        setConversations({ [newConversation.id]: newConversation });
        setSelectedConversationId(newConversation.id);
        return;
      }

      const formattedConversations = _formatFetchedConversations(fetchedConversations);

      const fetchedConversationsSortedByUpdatedAt = orderBy(
        formattedConversations,
        ({ updatedAt }) => new Date(updatedAt),
        'desc',
      );

      const lastConversation = fetchedConversationsSortedByUpdatedAt[0];

      setConversations(keyBy(formattedConversations, 'id'));

      setSelectedConversationId(lastConversation.id);
    } catch (error) {
      showErrorMessage(error.message);
    } finally {
      setIsConversationLoading(false);
    }
  };

  const fetchConversationMessages = async () => {
    const currentConversation = { ...conversations[selectedConversationId] };

    if (!currentConversation) {
      return;
    }

    setIsMessagesLoading(true);

    try {
      const { messages: fetchedMessages } = await agentService.getConversationMessages(
        currentConversation.id,
      );

      const formattedMessages = _formatFetchedMessages(fetchedMessages);

      setConversations({
        ...conversations,
        [currentConversation.id]: {
          ...currentConversation,
          messages: formattedMessages,
        },
      });

      setIsRetryInProgress(
        Object.values(formattedMessages).some(
          ({ status, has_retried }) => status === PROCESSING_MESSAGE_STATUS && has_retried,
        ),
      );
    } catch {
      showErrorMessage(i18next.t('AGENT.AGENT.CONVERSATION_FETCH_ERROR'));
    } finally {
      setIsMessagesLoading(false);
    }
  };

  const handleDeleteConversation = (conversationId) => {
    const actions = [
      GENERIC_MODAL_CANCEL_BUTTON(),
      {
        ...GENERIC_MODAL_CONFIRM_BUTTON(),
        handleClick: async () => {
          await deleteConversation(conversationId);
        },
      },
    ];

    const params = getConfirmationModal({
      title: i18next.t('AGENT.AGENT.DELETE_CONVERSATION_MODAL_TITLE'),
      icon: '/images/inpulse/warning-white-small.svg',
      actions,
      content: i18next.t('AGENT.AGENT.DELETE_CONVERSATION_MODAL_CONTENT'),
    });

    openGenericModal(params);
  };

  const deleteConversation = async (conversationId) => {
    const updatedConversations = { ...conversations };

    try {
      if (!get(updatedConversations, `${conversationId}.isNewConversation`, false)) {
        await agentService.deleteConversation(conversationId);
      }

      delete updatedConversations[conversationId];

      if (selectedConversationId === conversationId) {
        if (Object.keys(updatedConversations).length > 0) {
          const conversationsArray = Object.values(updatedConversations);

          const lastConversation = orderBy(
            conversationsArray,
            ({ updatedAt }) => new Date(updatedAt),
            'desc',
          )[0];

          setConversations(updatedConversations);
          setSelectedConversationId(lastConversation.id);
          return;
        }

        const newConversation = {
          id: uuidV4(),
          name: i18next.t('AGENT.AGENT.NEW_CHAT'),
          messages: {},
          createdAt: moment(new Date().toJSON()),
          updatedAt: moment(new Date().toJSON()),
          isNewConversation: true,
          active: true,
          favorite: false,
        };

        setConversations({ [newConversation.id]: newConversation });
        setSelectedConversationId(newConversation.id);
        return;
      }

      setConversations(updatedConversations);
    } catch {
      showErrorMessage(i18next.t('AGENT.AGENT.DELETE_CONVERSATION_ERROR'));
    }
  };

  const updateConversation = async (updatedConversation) => {
    const updatedConversations = { ...conversations };

    try {
      if (get(updatedConversations, `${updatedConversation.id}.isNewConversation`, false)) {
        return;
      }

      await agentService.updateConversation(updatedConversation);

      setConversations({ ...conversations, [updatedConversation.id]: updatedConversation });
    } catch {
      showErrorMessage(i18next.t('AGENT.AGENT.UPDATE_CONVERSATION_ERROR'));
    }
  };

  const startNewConversation = () => {
    const newConversation = {
      id: uuidV4(),
      name: i18next.t('AGENT.AGENT.NEW_CHAT'),
      messages: {},
      createdAt: moment(new Date().toJSON()),
      updatedAt: moment(new Date().toJSON()),
      isNewConversation: true,
      active: true,
      favorite: false,
    };

    setConversations({ ...conversations, [newConversation.id]: newConversation });
    setSelectedConversationId(newConversation.id);
  };

  const sendMessage = async (content = inputValue) => {
    if (dailyUsage === 100) {
      return;
    }

    const newMessageId = uuidV4();
    const newMessage = {
      id: newMessageId,
      content,
      isUser: true,
      status: PROCESSING_MESSAGE_STATUS,
      createdAt: moment(new Date().toJSON()),
      isLocalMessage: true,
    };

    setInputValue('');
    setConversations((prev) => ({
      ...prev,
      [selectedConversationId]: {
        ...prev[selectedConversationId],
        messages: { ...prev[selectedConversationId].messages, [newMessageId]: newMessage },
      },
    }));

    const response = await agentService.sendMessage({
      id: newMessageId,
      agent_conversation_id: selectedConversationId,
      content,
      currency_code: currency.alphabeticCode,
      enabled_feature_paths: enabledFeaturePaths,
    });

    setDailyUsage(response.daily_usage);

    setConversations((prev) => {
      const updatedConversations = { ...prev };
      const previousConversation = updatedConversations[selectedConversationId];

      updatedConversations[selectedConversationId] = {
        ...previousConversation,
        id: selectedConversationId,
        messages: {
          ...previousConversation.messages,
          [newMessageId]: {
            ...previousConversation.messages[newMessageId],
            status: response.user_message_status,
            isLocalMessage: false,
          },
          [response.id]: {
            id: response.id,
            content: response.content,
            chart: response.chart,
            isUser: false,
            createdAt: moment.utc(response.created_at),
            score: null,
            table: response.table,
            isLocalMessage: false,
          },
        },
        isNewConversation: false,
        name: get(response, 'conversation.name', i18next.t('AGENT.AGENT.NEW_CHAT')),
        updatedAt: moment.utc(get(response, 'conversation.updated_at')),
      };
      return updatedConversations;
    });
  };

  const handleExportTable = (table) => {
    const jsonData = parseMarkdownTable(table.data);

    const worksheet = XLSX.utils.json_to_sheet(jsonData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Feuille1');

    const formattedTitle = table.title.replace(/ /g, '_');

    XLSX.writeFile(workbook, `${formattedTitle}.xlsx`);
  };

  const handleFeedBack = async (messageId, score) => {
    const currentConversation = { ...conversations[selectedConversationId] };

    if (isEmpty(currentConversation)) {
      return;
    }

    try {
      const updatedMessages = {
        ...currentConversation.messages,
        [messageId]: {
          ...currentConversation.messages[messageId],
          score,
        },
      };

      const updatedConversation = {
        ...currentConversation,
        messages: updatedMessages,
      };

      setConversations({ ...conversations, [selectedConversationId]: updatedConversation });

      await agentService.sendFeedBack({
        message_id: messageId,
        score,
      });
    } catch {
      setConversations({ ...conversations, [selectedConversationId]: currentConversation });
      showErrorMessage(i18next.t('AGENT.AGENT.FEEDBACK_ERROR'));
    }
  };

  const handleRetryMessage = async (messageId) => {
    setConversations((prev) => ({
      ...prev,
      [selectedConversationId]: {
        ...prev[selectedConversationId],
        messages: {
          ...prev[selectedConversationId].messages,
          [messageId]: {
            ...prev[selectedConversationId].messages[messageId],
            status: PROCESSING_MESSAGE_STATUS,
            has_retried: true,
            isLocalMessage: true,
          },
        },
      },
    }));

    try {
      setIsRetryInProgress(true);
      const newMessage = await agentService.retryMessage(
        messageId,
        currency.alphabeticCode,
        enabledFeaturePaths,
      );

      setConversations((prev) => ({
        ...prev,
        [selectedConversationId]: {
          ...prev[selectedConversationId],
          messages: {
            ...prev[selectedConversationId].messages,
            [messageId]: {
              ...prev[selectedConversationId].messages[messageId],
              status: get(newMessage, 'user_message.status', null),
              has_retried: get(newMessage, 'user_message.has_retried', true),
            },
            [newMessage.id]: {
              id: newMessage.id,
              content: newMessage.content,
              chart: newMessage.chart,
              score: newMessage.score,
              table: newMessage.table,
              createdAt: moment.utc(newMessage.created_at),
              isLocalMessage: false,
              isUser: false,
            },
          },
        },
      }));
    } catch {
      setConversations((prev) => ({
        ...prev,
        [selectedConversationId]: {
          ...prev[selectedConversationId],
          messages: {
            ...prev[selectedConversationId].messages,
            [messageId]: {
              ...prev[selectedConversationId].messages[messageId],
              status: 'error',
              has_retried: true,
            },
          },
        },
      }));

      showErrorMessage(i18next.t('AGENT.AGENT.RETRY_ERROR'));
    } finally {
      setIsRetryInProgress(false);
    }
  };

  const isAgentTyping = () => {
    if (!selectedConversationId || !conversations[selectedConversationId]) {
      return false;
    }

    const conversationMessages = conversations[selectedConversationId].messages;
    if (!conversationMessages || Object.keys(conversationMessages).length === 0) {
      return false;
    }

    const messagesArray = Object.values(conversationMessages);

    const hasProcessingMessage = messagesArray.some(({ status, isUser, has_retried }) => {
      const isProcessing = status === PROCESSING_MESSAGE_STATUS;

      if (isProcessing && isUser && has_retried) {
        return false;
      }

      return isProcessing;
    });

    return hasProcessingMessage;
  };

  return (
    <ChatContainer>
      <ChatMessagesContainer>
        <Header
          conversationName={get(
            conversations,
            `${selectedConversationId}.name`,
            i18next.t('AGENT.AGENT.NEW_CHAT'),
          )}
        />
        <MessageList
          dailyUsage={dailyUsage}
          handleExportTable={handleExportTable}
          handleFeedBack={handleFeedBack}
          handleRetryMessage={handleRetryMessage}
          isAiTyping={isAgentTyping()}
          isConversationLoading={isConversationLoading}
          isMessagesLoading={isMessagesLoading}
          messages={get(conversations, `${selectedConversationId}.messages`, {})}
          sendMessage={sendMessage}
        />
        <InputArea
          dailyUsage={dailyUsage}
          inputValue={inputValue}
          isAiTyping={isAgentTyping()}
          isConversationLoading={isConversationLoading}
          isMessagesLoading={isMessagesLoading}
          isRetryInProgress={isRetryInProgress}
          sendMessage={sendMessage}
          setInputValue={setInputValue}
        />
      </ChatMessagesContainer>
      <ConversationList
        conversations={conversations}
        currentConversationId={selectedConversationId}
        dailyUsage={dailyUsage}
        deleteConversation={handleDeleteConversation}
        setSelectedConversationId={setSelectedConversationId}
        startNewConversation={startNewConversation}
        updateConversation={updateConversation}
      />
    </ChatContainer>
  );
};

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
});

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
  userRights: state.baseReducer.userRights,
});

export default connect(mapStateToProps, mapDispatchToProps)(InpulseAgent);
